"use client";

import Image from "next/image";

import { cn } from "@workspace/ui";
import { buttonVariants } from "@workspace/ui/components/button";
import { Icons } from "@workspace/ui/components/icons";

import { CtaButton } from "~/components/common/cta/button";
import { TurboLink } from "~/components/common/turbo-link";
import { Discount } from "~/components/marketing/home/core/pricing/layout/discount";
import { useAnalytics } from "~/lib/analytics";
import { Product } from "~/lib/constants/products";
import { REVIEWS } from "~/lib/constants/reviews";
import { DEMO_URL } from "~/lib/constants/urls";
import { useDiscount } from "~/lib/discount";
import { useActiveProduct } from "~/lib/hooks/use-active-product";
import { useTotalLicenses } from "~/lib/hooks/use-total-licenses";

export const HeroDiscount = () => {
  const product = useActiveProduct();
  const discount = useDiscount({ product });

  if (!discount) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <div className="relative inline-flex">
        <div className="bg-primary inline-block size-2 rounded-full" />
        <div className="bg-primary absolute size-2 animate-ping rounded-full" />
      </div>
      <Discount.Text
        className="text-muted-foreground text-sm"
        product={product}
      />
    </div>
  );
};

export const HeroReviews = () => {
  const totalLicenses = useTotalLicenses();

  return (
    <a
      href="#reviews"
      className="mt-3 flex items-center justify-center gap-2 md:mt-4 md:gap-3"
    >
      <div className="flex">
        {REVIEWS.slice(0, 5).map((review, index) => (
          <Image
            key={index}
            src={review.author.avatar}
            alt={review.author.name}
            width={50}
            height={50}
            sizes="48px"
            className="border-background -ml-4 size-10 rounded-full border-2 select-none md:size-12"
          />
        ))}

        <div className="border-background bg-foreground -ml-4 flex size-10 items-center justify-center rounded-full border-2 p-2.5 md:size-12 md:p-3">
          <Icons.Vercel className="text-background w-full -translate-y-px" />
        </div>
      </div>
      <div className="mt-1 flex flex-col items-start gap-px">
        <div className="-ml-0.5 flex items-center justify-start">
          {Array.from({
            length: Math.ceil(
              REVIEWS.reduce((acc, review) => acc + review.rating, 0) /
                REVIEWS.length,
            ),
          }).map((_, index) => (
            <Icons.Star
              key={index}
              className="size-5 md:size-5.5"
              aria-label="Star"
            />
          ))}
        </div>
        <span className="text-muted-foreground text-xs font-medium md:text-sm">
          trusted by <span className="text-foreground">{totalLicenses}+</span>{" "}
          founders
        </span>
      </div>
    </a>
  );
};

export const HeroBadge = ({ href, title }: { href: string; title: string }) => {
  const { track } = useAnalytics();

  return (
    <TurboLink
      onClick={() => track("hero_badge_click", { href, title })}
      href={href}
      rel="noopener noreferrer"
      target="_blank"
      className="border-border/50 bg-card shadow-primary/15 hover:bg-muted/50 dark:border-border dark:hover:bg-muted/65 group mt-2 mb-6 flex max-w-full items-center gap-2 rounded-full border px-2 py-1.5 pr-4 text-xs shadow-[0px_12px_32px_0px] transition-colors sm:mb-2 sm:text-sm"
    >
      <div className="bg-muted flex gap-1 rounded-full px-3 py-1">
        <Icons.Sparkles className="w-4" />
        <span>New</span>
      </div>
      <span className="truncate leading-tight font-medium">{title}</span>
      <Icons.ArrowRight className="w-4 transition-transform group-hover:translate-x-1" />
    </TurboLink>
  );
};

export const HeroDemoButton = ({
  href,
  source,
}: {
  href?: string;
  source?: string;
}) => {
  const { track } = useAnalytics();
  const product = useActiveProduct();

  return (
    <a
      href={
        href ??
        (product in DEMO_URL
          ? DEMO_URL[product as keyof typeof DEMO_URL].web
          : DEMO_URL[Product.CORE].web)
      }
      target="_blank"
      className={cn(
        buttonVariants({ variant: "outline" }),
        "h-auto w-full gap-2 px-4 py-3.5 md:w-fit md:gap-3 md:px-5 md:py-4 lg:px-7 lg:text-lg",
      )}
      onClick={() =>
        track("demo", { source: source ?? "hero", id: "web", product })
      }
    >
      <Icons.LinkSquare className="size-4 md:size-4.5" strokeWidth={2} /> See
      demo
    </a>
  );
};

export const HeroCtaButton = ({
  className,
  ...props
}: React.ComponentProps<typeof CtaButton>) => (
  <CtaButton
    trigger="cta"
    className={cn(
      "h-auto w-full px-8 py-3.5 md:w-fit md:px-10 md:py-4 lg:px-12 lg:text-lg",
      className,
    )}
    {...props}
  />
);
